import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { IconButton, InputAdornment, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useIntl } from 'react-intl';
import MDInput from 'components/MDInput';

/**
 * CodeInput component enforcing uppercase letters, digits, and underscores only.
 * Converts lowercase to uppercase, spaces to underscores, and prevents pasting.
 *
 * @component
 * @param {object} props.field - Field properties for the input (e.g., value, onChange).
 * @param {object} props.errors - Form errors, with optional message for code field.
 * @param {Function} props.clearField - Function to clear the code field's value.
 */
const CodeInput = forwardRef(({ field, errors, clearField }, ref) => {
  const { formatMessage: msg } = useIntl();

  const handleInputChange = (event) => {
    const { selectionStart, selectionEnd, value } = event.target;
    const sanitizedValue = value
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');

    if (sanitizedValue !== value) {
      // Ensure the caret position is adjusted after sanitization
      const caretOffset = sanitizedValue.length - value.length;
      field.onChange({
        ...event,
        target: { ...event.target, value: sanitizedValue }
      });
      setTimeout(() => {
        event.target.setSelectionRange(selectionStart + caretOffset, selectionEnd + caretOffset);
      }, 0);
    } else {
      field.onChange(event);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasteContent = event.clipboardData.getData('text');
    const sanitizedValue = pasteContent
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');
    field.onChange({
      ...event,
      target: { ...event.target, value: sanitizedValue }
    });
  };

  return (
    <MDInput
      required
      {...field}
      label={msg({ id: 'field.code.label' })}
      variant="filled"
      fullWidth
      size="small"
      error={!!errors.code}
      helperText={errors.code ? errors.code.message : ''}
      onChange={handleInputChange}
      onPaste={handlePaste}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={msg({ id: 'tooltip.clear' })}>
              <IconButton onClick={clearField}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        )
      }}
      ref={ref}
    />
  );
});

CodeInput.propTypes = {
  field: PropTypes.object.isRequired,
  errors: PropTypes.shape({
    code: PropTypes.shape({
      message: PropTypes.string
    })
  }),
  clearField: PropTypes.func.isRequired
};

export default CodeInput;
